/**
 * Saved Explore experiences ("hearts"), in browser storage, scoped to the person
 * AND the trip they belong to.
 *
 * They were component state in ExploreTab, and the Explore tab unmounts on every
 * tab switch: a heart lasted until the user looked at anything else (JAR-2003).
 *
 * Trip-scoped as well as owner-scoped because a heart is about one trip's
 * shortlist. Rome's saved tours appearing on a Lisbon trip is not a leak, but it
 * is wrong, and keying by trip costs nothing.
 *
 * The shape follows localSettings and localTrips deliberately: same `jarvis_`
 * prefix form, owner passed in rather than inferred, failures swallowed. The
 * prefix is what puts these keys inside localDataPurge's sweep, so a sign-out
 * erases them without this module being listed anywhere.
 *
 * A live set is held in memory too, and that is what makes a blocked-storage
 * session work: in a private window `setItem` throws, and without the mirror the
 * heart would refuse to fill at all (JAR-1693, the same lesson one feature over).
 * That mirror is user data outside storage, so `purgeAllLocalUserData` clears it
 * with `clearExploreFavorites`, exactly as it clears planIntent's: sign-out,
 * `auth:expired` and delete-account all purge in place and only navigate, so
 * without that call the same page session would hand the next sign-in the
 * previous person's hearts (#535 review).
 */

import type { TripsOwner } from './localTrips';

const KEY_PREFIX = 'jarvis_explore_favorites:';

/** What THIS session has saved, by `favoritesKey` — the fallback that keeps the
 *  feature working when storage refuses the write. Only a toggle writes here, so
 *  a read stays free of side effects (#535 review). */
const held = new Map<string, Set<string>>();

/** Drop the in-memory hearts. Called by purgeAllLocalUserData, because a purge
 *  that only sweeps storage leaves this map authoritative for the rest of the
 *  page session. */
export function clearExploreFavorites(): void {
  held.clear();
}

const trimmed = (value: string | null | undefined): string | null =>
  value && value.trim() ? value.trim() : null;

/**
 * The identity of one trip's hearts, for one person. Stable and safe to compare:
 * a component holds it beside the set so a trip switch is visible as a mismatch
 * rather than as one trip's hearts painted under another.
 */
export function favoritesKey(owner: TripsOwner, tripId?: string): string {
  // Each part escaped, so a colon inside an id cannot join two scopes into one
  // key. Nothing validates these ids here, and the consequence of a collision is
  // one person's hearts read under another's scope — the leak this module exists
  // to prevent (#535 review round 3).
  return `${encodeURIComponent(trimmed(owner) ?? '')}:${encodeURIComponent(trimmed(tripId) ?? '')}`;
}

/** The storage key, or null when there is no owner and trip to scope by — in
 *  which case the hearts live in memory for the session and are never written
 *  under a shared key. Exported the way settingsStorageKey is: the test that
 *  plants an unparseable row has to address the same key this module reads. */
export function favoritesStorageKey(owner: TripsOwner, tripId?: string): string | null {
  const id = trimmed(owner);
  const trip = trimmed(tripId);
  return id && trip ? `${KEY_PREFIX}${encodeURIComponent(id)}:${encodeURIComponent(trip)}` : null;
}

/** This trip's saved experience ids. Empty when there are none, never null: an
 *  unreadable or corrupt row is the same as no hearts. */
export function readExploreFavorites(owner: TripsOwner, tripId?: string): Set<string> {
  // This runs on the render that changes trip, so it may not write anything:
  // caching the parsed row here made a render mutate module state, which
  // StrictMode's double render and concurrent rendering are entitled to punish
  // (#535 review). A parse of a handful of ids is not worth that.
  const live = held.get(favoritesKey(owner, tripId));
  if (live) return live;

  const key = favoritesStorageKey(owner, tripId);
  const ids = new Set<string>();
  if (key) {
    try {
      const raw = localStorage.getItem(key);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) {
        for (const id of parsed) if (typeof id === 'string' && id) ids.add(id);
      }
    } catch {
      /* storage disabled, or a row we cannot parse: no hearts, not a crash */
    }
  }
  return ids;
}

/**
 * Move a trip's hearts to the id it has now.
 *
 * A trip the planner built locally is `local-<slug>-<hash>` until it reaches core,
 * which mints a UUID and the app adopts it (`moveLocalTrips`). The hearts were
 * keyed on the old id, so without this they vanish from the tab at the moment the
 * trip becomes real, and the old row sits in storage until the next purge
 * (#535 review round 4).
 *
 * Nothing is overwritten: a heart already saved under the new id wins, because it
 * describes the trip as it stands now.
 */
export function moveExploreFavorites(owner: TripsOwner, fromTripId: string, toTripId: string): void {
  // One id to itself would write the merged set and then delete the row it just
  // wrote, taking the whole shortlist with it. `moveLocalTrips` cannot ask for
  // that (a `local-` id against core's UUID), but this is exported (#541 review).

  if (favoritesKey(owner, fromTripId) === favoritesKey(owner, toTripId)) return;

  const from = readExploreFavorites(owner, fromTripId);
  // Nothing to write is not nothing to do: an empty row, left by un-hearting a
  // trip's last experience, is exactly the residue this clears (#541 review).
  let persisted = true;

  if (from.size > 0) {
    const merged = new Set([...readExploreFavorites(owner, toTripId), ...from]);
    held.set(favoritesKey(owner, toTripId), merged);
    const toKey = favoritesStorageKey(owner, toTripId);
    if (toKey) {
      persisted = false;
      try {
        localStorage.setItem(toKey, JSON.stringify([...merged]));
        persisted = true;
      } catch {
        /* quota or a private window: the set above still holds for this session */
      }
    }
  }

  // The old row is the last persisted copy when the new one could not be written,
  // and `held` does not survive a reload, so a failed write keeps it (#541 review).
  if (!persisted) return;

  held.delete(favoritesKey(owner, fromTripId));
  const fromKey = favoritesStorageKey(owner, fromTripId);
  if (fromKey) {
    try {
      localStorage.removeItem(fromKey);
    } catch {
      /* nothing reads it any more either way */
    }
  }
}

/** Save or unsave one experience, and answer with the trip's new set. */
export function toggleExploreFavorite(owner: TripsOwner, tripId: string | undefined, id: string): Set<string> {
  const next = new Set(readExploreFavorites(owner, tripId));
  if (next.has(id)) next.delete(id);
  else next.add(id);

  held.set(favoritesKey(owner, tripId), next);
  const key = favoritesStorageKey(owner, tripId);
  if (key) {
    try {
      localStorage.setItem(key, JSON.stringify([...next]));
    } catch {
      /* private window, or storage full: the set above still holds for this session */
    }
  }
  return next;
}
